import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { login, forgotPassword } from '../actions/authActions';
import { clearErrors } from '../actions/errorActions';
import { Eye, EyeOff, ArrowLeft, Target, TrendingUp, Lightbulb, Rocket } from 'lucide-react';

const LoginPage = ({ auth: { isAuthenticated }, error, forgotPasswordMessage, login, forgotPassword, clearErrors }) => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [msg, setMsg] = useState(null);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMsg, setForgotMsg] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) navigate('/');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (error.id === 'LOGIN_FAIL') {
      const m = typeof error.msg === 'string' ? error.msg : error.msg?.msg || 'Login failed. Please try again.';
      setMsg(m);
    } else {
      setMsg(null);
    }
  }, [error]);

  useEffect(() => {
    if (forgotPasswordMessage) setForgotMsg(forgotPasswordMessage);
  }, [forgotPasswordMessage]);

  useEffect(() => {
    return () => clearErrors();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    login({ email, password });
  };

  const handleForgotSubmit = (e) => {
    e.preventDefault();
    if (forgotEmail.trim()) forgotPassword({ email: forgotEmail });
  };

  const features = [
    { icon: Target,     text: 'Set and track your learning goals' },
    { icon: TrendingUp, text: 'Monitor your progress in real time' },
    { icon: Lightbulb,  text: 'Access courses built for your path' },
    { icon: Rocket,     text: 'Achieve more with structured learning' },
  ];

  return (
    <div className="min-h-screen flex">

      {/* Left — Branding Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#054581] to-[#1086a4] flex-col justify-between p-12 text-white">
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-1">Your Big Year</h1>
          <p className="text-white/70 text-sm">Learning Management System</p>
        </div>

        <div className="space-y-4">
          <h2 className="text-2xl font-semibold leading-snug">
            Everything you need<br />to grow and succeed.
          </h2>
          <div className="space-y-3 pt-2">
            {features.map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-2 shrink-0">
                  <Icon className="w-4 h-4" />
                </div>
                <span className="text-sm text-white/90">{text}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/50 text-xs">powered by Your Big Year®</p>
      </div>

      {/* Right — Form Panel */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12 bg-white">
        <div className="w-full max-w-md">

          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <h1 className="text-2xl font-bold text-[#054581]">Your Big Year</h1>
            <p className="text-gray-500 text-sm">Learning Management System</p>
          </div>

          {!showForgot ? (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-[#054581]">Welcome back</h2>
                <p className="text-gray-500 mt-1">Sign in to your account to continue</p>
              </div>

              {msg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
                  {msg}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => { setShowForgot(true); setMsg(null); }}
                    className="text-sm text-[#1086a4] hover:text-[#054581] font-medium transition-colors"
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-[#1086a4] to-[#054581] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200"
                >
                  Sign In
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-gray-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#1086a4] font-semibold hover:text-[#054581] transition-colors">
                  Register
                </Link>
              </p>
            </>
          ) : (
            <>
              <button
                onClick={() => { setShowForgot(false); setForgotEmail(''); setForgotMsg(null); }}
                className="flex items-center gap-2 text-gray-500 hover:text-[#1086a4] mb-8 transition-colors text-sm"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>

              <div className="mb-8">
                <h2 className="text-3xl font-bold text-[#054581]">Reset password</h2>
                <p className="text-gray-500 mt-1">Enter your email and we'll send you a new password.</p>
              </div>

              {forgotMsg && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-600 rounded-lg text-sm">
                  {forgotMsg}
                </div>
              )}

              <form onSubmit={handleForgotSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#1086a4] focus:border-[#1086a4] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-gradient-to-r from-[#1086a4] to-[#054581] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-200"
                >
                  Send New Password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  error: state.error,
  forgotPasswordMessage: state.auth.forgotPasswordMessage
});

export default connect(mapStateToProps, { login, forgotPassword, clearErrors })(LoginPage);
